import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Globe, CheckCircle, AlertTriangle, Smartphone, Wifi, Tablet, MapPin } from "lucide-react";
import { providers, usps as mobielUsps } from "@/data/mobiel";
import { BundelCard, BelBuitenEUCard } from "@/components/mobiel/BundelCards";
import logoKpn from "@/assets/logo-kpn.png";
import mobielHeroBg from "@/assets/mobiel-hero-bg.jpg";
import mobielHeroVideo from "@/assets/mobiel-hero-video.mp4";

const provider = providers.find((p) => p.slug === "kpn")!;

const MobielKPNPage = () => {
  const hasBuitenland = provider.buitenlandbundels && provider.buitenlandbundels.length > 0;

  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[40vh] flex items-center overflow-hidden">
        <video autoPlay muted loop playsInline poster={mobielHeroBg} className="absolute inset-0 h-full w-full object-cover">
          <source src={mobielHeroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${provider.kleur}cc, ${provider.kleur}88 50%, rgba(0,0,0,0.4))` }} />
        <div className="absolute right-[-4%] top-1/2 -translate-y-1/2 pointer-events-none select-none opacity-[0.08]">
          <img loading="eager" decoding="async" fetchPriority="high" src={logoKpn} alt="" className="w-[340px] md:w-[480px] h-auto rotate-[-12deg] brightness-0 invert" />
        </div>
        <div className="container relative z-10 py-16">
          <Link to="/diensten/mobiel" className="inline-flex items-center text-white/80 hover:text-white mb-6 text-sm transition-colors">
            <ArrowLeft className="mr-2 h-4 w-4" /> Terug naar overzicht
          </Link>
          <motion.div
            className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white p-2.5 shadow-lg"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}>
            
            <img loading="lazy" decoding="async" src={logoKpn} alt="KPN logo" className="w-full h-full object-contain" />
          </motion.div>
          <motion.h1 className="text-3xl md:text-5xl font-bold text-white" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {provider.naam} Zakelijk Mobiel
          </motion.h1>
          <motion.p className="text-white/80 mt-2 max-w-lg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            {provider.beschrijving}
          </motion.p>
          <motion.div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            {mobielUsps.map((usp, i) =>
            <div key={i} className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${i === 0 ? "bg-primary" : i === 1 ? "bg-rinqel-blue" : i === 2 ? "bg-rinqel-green" : "bg-primary"}`} />
                <span className="text-white/90 text-sm font-medium">{usp.titel}</span>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Abonnementen */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl" style={{ backgroundColor: `${provider.kleur}15` }}>
                <Smartphone className="h-5 w-5" style={{ color: provider.kleur }} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">Sim Only — Mobiel</h2>
            </div>
            <p className="text-muted-foreground mb-8 ml-[52px]">Bellen, sms'en en internetten op je smartphone · Maandelijks opzegbaar</p>

            {(() => {
              const basisBundels = provider.abonnementen.slice(0, 3);
              const unlimitedBundels = provider.abonnementen.slice(3);
              const hasUnlimited = unlimitedBundels.length > 0;
              return (
                <>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
                    {(hasUnlimited ? basisBundels : provider.abonnementen).map((abo, i) =>
                    <motion.div key={abo.naam} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                        <Card className="h-full relative overflow-hidden transition-shadow hover:shadow-lg" style={{ borderColor: provider.kleur, boxShadow: `0 0 0 2px ${provider.kleur}` }}>
                          <div className="absolute top-0 left-0 right-0 text-center text-xs font-semibold text-white py-1 flex items-center justify-center gap-1.5" style={{ backgroundColor: provider.kleur }}>
                            <Smartphone className="h-3 w-3" />
                            {abo.naam}
                          </div>
                          <CardContent className="p-5 flex flex-col items-center text-center pt-9 h-full">
                            <div className="text-2xl font-bold mb-1" style={{ color: provider.kleur }}>{abo.prijs}</div>
                            <span className="text-xs text-muted-foreground mb-4">per maand</span>
                            <div className="w-full space-y-2 text-sm text-left flex-1">
                              <div className="flex items-center gap-2">
                                <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                <span><strong>{abo.data}</strong> data</span>
                              </div>
                              {abo.kenmerken?.map((kenmerk, ki) =>
                            <div key={ki} className="flex items-center gap-2">
                                  <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                  <span className="text-xs">{kenmerk}</span>
                                </div>
                            ) ||
                            <>
                                  <div className="flex items-center gap-2">
                                    <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                    <span><strong>{abo.minuten}</strong> bellen & sms</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                    <span>EU-roaming inbegrepen</span>
                                  </div>
                                </>
                            }
                            </div>
                            <Button asChild size="sm" className="w-full mt-4" style={{ backgroundColor: provider.kleur, color: 'white' }}>
                              <TrackedLink to="/contact" trackLabel="Aanvragen" trackLocation="mobiel-kpn-bundel">Aanvragen</TrackedLink>
                            </Button>
                          </CardContent>
                        </Card>
                      </motion.div>
                    )}
                  </div>

                  {hasUnlimited &&
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      {unlimitedBundels.map((abo, i) =>
                    <motion.div key={abo.naam} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                          <Card className={`h-full relative overflow-hidden transition-shadow hover:shadow-lg ${abo.populair ? 'ring-2 ring-offset-2' : ''}`} style={{ borderColor: provider.kleur, boxShadow: `0 0 0 2px ${provider.kleur}`, ...(abo.populair ? { ['--tw-ring-color' as string]: provider.kleur } : {}) }}>
                            {abo.populair &&
                        <div className="absolute top-0 left-0 right-0 text-center text-[10px] font-bold uppercase tracking-wider text-white py-0.5" style={{ backgroundColor: provider.kleur }}>
                                ⭐ Meest gekozen
                              </div>
                        }
                            <div className={`${abo.populair ? 'mt-5' : ''} absolute top-0 left-0 right-0 text-center text-xs font-semibold text-white py-1 flex items-center justify-center gap-1.5`} style={{ backgroundColor: provider.kleur }}>
                              <Smartphone className="h-3 w-3" />
                              {abo.naam}
                            </div>
                            <CardContent className={`p-6 flex flex-col items-center text-center ${abo.populair ? 'pt-14' : 'pt-10'} h-full`}>
                              <div className="text-3xl font-bold mb-1" style={{ color: provider.kleur }}>{abo.prijs}</div>
                              <span className="text-xs text-muted-foreground mb-6">per maand</span>
                              <div className="w-full space-y-3 text-sm text-left flex-1">
                                <div className="flex items-center gap-2">
                                  <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                  <span><strong>{abo.data}</strong> data</span>
                                </div>
                                {abo.kenmerken?.map((kenmerk, ki) =>
                            <div key={ki} className="flex items-center gap-2">
                                    <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                    <span>{kenmerk}</span>
                                  </div>
                            ) ||
                            <>
                                    <div className="flex items-center gap-2">
                                      <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                      <span><strong>{abo.minuten}</strong> bellen & sms</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                      <span>EU-roaming inbegrepen</span>
                                    </div>
                                  </>
                            }
                              </div>
                              <Button asChild className="w-full mt-6" style={{ backgroundColor: provider.kleur, color: 'white' }}>
                                <TrackedLink to="/contact" trackLabel="Aanvragen" trackLocation="mobiel-kpn-unlimited">Aanvragen</TrackedLink>
                              </Button>
                            </CardContent>
                          </Card>
                        </motion.div>
                    )}
                    </div>
                  }
                </>);

            })()}
          </div>

          {/* Data Only — Mobiel Internet */}
          {provider.tabletAbonnementen && provider.tabletAbonnementen.length > 0 &&
          <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl" style={{ backgroundColor: `${provider.kleur}15` }}>
                  <Wifi className="h-5 w-5" style={{ color: provider.kleur }} />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">Data Only — Mobiel Internet</h2>
              </div>
              <p className="text-muted-foreground mb-8 ml-[52px]">Mobiel internet voor tablets, laptops en IoT-apparaten · Alleen data, alleen in Nederland</p>

              {/* Rij 1: eerste 3 bundels */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
                {provider.tabletAbonnementen.slice(0, 3).map((abo, i) =>
              <motion.div key={abo.naam} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                    <Card className="h-full relative overflow-hidden transition-shadow hover:shadow-lg border-dashed border-2" style={{ borderColor: `${provider.kleur}60` }}>
                      <div className="absolute top-0 left-0 right-0 text-center text-xs font-semibold py-1 flex items-center justify-center gap-1.5" style={{ backgroundColor: `${provider.kleur}15`, color: provider.kleur }}>
                        <Wifi className="h-3 w-3" />
                        {abo.naam}
                      </div>
                      <CardContent className="p-5 flex flex-col items-center text-center pt-9 h-full">
                        <div className="text-2xl font-bold mb-1" style={{ color: provider.kleur }}>{abo.prijs}</div>
                        <span className="text-xs text-muted-foreground mb-4">per maand</span>
                        <div className="w-full space-y-2 text-sm text-left flex-1">
                          <div className="flex items-center gap-2">
                            <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                            <span><strong>{abo.data}</strong> data</span>
                          </div>
                          {abo.kenmerken?.map((kenmerk, ki) =>
                      <div key={ki} className="flex items-center gap-2">
                              <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                              <span className="text-xs">{kenmerk}</span>
                            </div>
                      )}
                        </div>
                        <Button asChild variant="outline" size="sm" className="w-full mt-4" style={{ borderColor: provider.kleur, color: provider.kleur }}>
                          <TrackedLink to="/contact" trackLabel="Aanvragen" trackLocation="mobiel-kpn-data-only">Aanvragen</TrackedLink>
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
              )}
              </div>

              {/* Rij 2: laatste 3 bundels */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
                {provider.tabletAbonnementen.slice(3).map((abo, i) =>
              <motion.div key={abo.naam} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                    <Card className="h-full relative overflow-hidden transition-shadow hover:shadow-lg border-dashed border-2" style={{ borderColor: `${provider.kleur}60` }}>
                      <div className="absolute top-0 left-0 right-0 text-center text-xs font-semibold py-1 flex items-center justify-center gap-1.5" style={{ backgroundColor: `${provider.kleur}15`, color: provider.kleur }}>
                        <Wifi className="h-3 w-3" />
                        {abo.naam}
                      </div>
                      <CardContent className="p-5 flex flex-col items-center text-center pt-9 h-full">
                        <div className="text-2xl font-bold mb-1" style={{ color: provider.kleur }}>{abo.prijs}</div>
                        <span className="text-xs text-muted-foreground mb-4">per maand</span>
                        <div className="w-full space-y-2 text-sm text-left flex-1">
                          <div className="flex items-center gap-2">
                            <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                            <span><strong>{abo.data}</strong> data</span>
                          </div>
                          {abo.kenmerken?.map((kenmerk, ki) =>
                      <div key={ki} className="flex items-center gap-2">
                              <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                              <span className="text-xs">{kenmerk}</span>
                            </div>
                      )}
                        </div>
                        <Button asChild variant="outline" size="sm" className="w-full mt-4" style={{ borderColor: provider.kleur, color: provider.kleur }}>
                          <TrackedLink to="/contact" trackLabel="Aanvragen" trackLocation="mobiel-kpn-data-only">Aanvragen</TrackedLink>
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
              )}
              </div>

              {/* Vast IP-adres optie */}
              <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <div className="rounded-xl border border-dashed border-border bg-muted/30 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4" style={{ color: provider.kleur }} />
                    <div>
                      <p className="text-sm font-semibold">Aanvullende optie: Vast IP-adres</p>
                      <p className="text-xs text-muted-foreground">Krijg een vast IP-adres op je data-only simkaart</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold" style={{ color: provider.kleur }}>€ 6,00 /per maand </span>
                </div>
              </motion.div>
            </div>
          }
        </div>
      </section>

      {/* Buitenlandbundels */}
      {hasBuitenland &&
      <section className="py-16 md:py-24 bg-muted/30">
          <div className="container">
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <span className="h-1 w-10 rounded-full" style={{ background: provider.kleur }} />
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: provider.kleur }}>Internationaal</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">
                Buitenlandbundels <span className="text-gradient">Zakelijk Mobiel</span>
              </h2>
              <p className="mt-2 text-muted-foreground max-w-2xl">
                Voorkom onverwachte kosten op zakenreis. Activeer vooraf een buitenlandbundel en betaal alleen bij gebruik.
              </p>
            </div>

            {/* EU Roaming blok */}
            <div className="mb-8 rounded-2xl overflow-hidden shadow-lg">
              <div className="p-6 md:p-8" style={{ background: `linear-gradient(135deg, ${provider.kleur}, ${provider.kleur}cc)` }}>
                <div className="flex items-center gap-3 mb-3">
                  <Globe className="h-6 w-6 text-white" />
                  <h3 className="text-xl md:text-2xl font-bold text-white">Voordelig mobiel bellen en internetten in het buitenland</h3>
                </div>
                <p className="text-white/90 mb-6 max-w-3xl">
                  Alle mobiele abonnementen binnen KPN Zakelijk Mobiel zijn binnen de EU net zo te gebruiken als in Nederland. Op de mobiele abonnementen zijn de voorwaarden van Roam like Home van toepassing.
                </p>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 md:p-6">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Check className="h-5 w-5" />
                    Gebruik je abonnement in deze landen tegen dezelfde tarieven
                  </h4>
                  <p className="text-white/85 text-sm leading-relaxed">
                    Andorra, België, Bulgarije, Cyprus (Griekse deel), Denemarken, Duitsland, Estland, Faeröer, Finland, Frankrijk, Frans-Guyana, Gibraltar, Griekenland, Guadeloupe, Guernsey, Hongarije, Ierland, IJsland, Isle of Man, Italië, Jersey, Kroatië, Letland, Liechtenstein, Litouwen, Luxemburg, Malta, Martinique, Mayotte, Monaco, Moldavië, Noorwegen, Noordzee (TAMPNET), Oekraïne, Oostenrijk, Polen, Portugal (incl. Azoren & Madeira), la Réunion, Roemenië, San Marino, Slovenië, Slowakije, Spanje (incl. Canarische eilanden), St. Martin, Tsjechië, Vaticaanstad, Verenigd Koninkrijk, Zweden, Zwitserland
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-8 lg:grid-cols-1">
              {provider.buitenlandbundels!.map((bundel, i) =>
            <BundelCard key={bundel.naam} bundel={bundel} index={i} providerKleur={provider.kleur} />
            )}
              {provider.belBuitenEU && <BelBuitenEUCard data={provider.belBuitenEU} providerKleur={provider.kleur} />}
            </div>

            <p className="mt-6 text-xs text-muted-foreground max-w-2xl">
              Deze bundels staan bij ons standaard altijd aan. Prijzen zijn exclusief BTW.
            </p>
          </div>
        </section>
      }

      {/* Extra mogelijkheden - Data SIM & Multisim */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-1 w-10 rounded-full" style={{ background: provider.kleur }} />
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: provider.kleur }}>Extra mogelijkheden</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold">
              Los af te sluiten <span className="text-gradient">opties</span>
            </h2>
            <p className="mt-2 text-muted-foreground max-w-2xl">
              Beide opties kosten € 6,00 per maand, zijn maandelijks opzegbaar en zonder simkaartkosten.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Data SIM */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Card className="h-full hover:shadow-lg transition-shadow overflow-hidden">
                <div className="px-6 py-4" style={{ background: `linear-gradient(135deg, ${provider.kleur}, ${provider.kleur}cc)` }}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-white">Data SIM</h3>
                    <Badge className="bg-white/20 text-white border-white/30 font-semibold">€ 6,00 per maand</Badge>
                  </div>
                </div>
                <CardContent className="p-6 space-y-4">
                  <p className="text-sm text-muted-foreground">Naast jouw KPN Zakelijk Mobiel abonnementen kun je 2 Data SIM's per abonnement afnemen. De Data SIM is bedoeld voor gebruik in o.a. laptops en tablets.</p>
                  <div className="rounded-xl bg-muted/30 border border-border p-4 space-y-2">
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: provider.kleur }} />
                      <span>Verbruikt data uit het tegoed van het bijbehorende abonnement</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: provider.kleur }} />
                      <span>Bij Unlimited: individueel tegoed van 25 GB per maand (NL/EU)</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: provider.kleur }} />
                      <span>Maximaal 2 Data SIM's per abonnement</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Multisim */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <Card className="h-full hover:shadow-lg transition-shadow overflow-hidden">
                <div className="px-6 py-4" style={{ background: `linear-gradient(135deg, ${provider.kleur}, ${provider.kleur}cc)` }}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-white">Multisim</h3>
                    <Badge className="bg-white/20 text-white border-white/30 font-semibold">€ 6,00 per maand</Badge>
                  </div>
                </div>
                <CardContent className="p-6 space-y-4">
                  <p className="text-sm text-muted-foreground">Bij elk KPN Zakelijk Mobiel abonnement kun je 1 Multisim afnemen. De Multisim koppelt je mobiele abonnement aan je smartwatch (Apple en Samsung).</p>
                  <div className="rounded-xl bg-muted/30 border border-border p-4 space-y-2">
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: provider.kleur }} />
                      <span>Verbruikt belminuten en data vanuit het gekoppelde abonnement</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: provider.kleur }} />
                      <span>Maximaal 1 Multisim per abonnement</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
                      <span>Niet mogelijk i.c.m. Vast-Mobiel integratie (wel met Anywhere)</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" style={{ background: `linear-gradient(135deg, ${provider.kleur}, ${provider.kleur}cc)` }}>
        <div className="container text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Direct aan de slag met {provider.naam}?</h2>
          <p className="text-white/80 mb-8 max-w-lg mx-auto">Neem contact op en we regelen alles voor je. Geen aansluitkosten, geen gedoe.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="hero-outline" className="border-white text-white hover:bg-white/10">
              <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="mobiel-kpn-cta">Neem contact op <ArrowRight className="ml-2 h-4 w-4" /></TrackedLink>
            </Button>
            <Button asChild size="lg" variant="ghost" className="text-white hover:bg-white/10">
              <Link to="/diensten/mobiel"><ArrowLeft className="mr-2 h-4 w-4" /> Alle netwerken bekijken</Link>
            </Button>
          </div>
        </div>
      </section>
    </>);

};

export default MobielKPNPage;